
import { Options, Vue } from "vue-class-component";
import { namespace } from 'vuex-class';
import QtsSemanalService from "@/service/v1/sigap/QtsSemanalService";
import SiteUtils from '@/assets/ts/aplicacao/site';
import { PerfilConfig } from "@/core/config/PerfisConfig";
import { labelStatusQts, statusQtsId, podeGerar, podeValidar, podePublicar, podeCancelar } from "./constantes";

const authStore = namespace('auth');

@Options({
    name: "QtsSemanalShow",
    components: {},
})
export default class QtsSemanalShow extends Vue {

    @authStore.Getter
    public perfis!: any

    @authStore.Getter
    public token!: any

    public service = new QtsSemanalService();
    public model: any = null;
    public processando = false;
    public reloadGrade = false;
    public diagnostico: any = this.diagnosticoVazio();

    private diagnosticoVazio() {
        return { titulo: '', ok: true, mensagem: '', meta: '', erros: [], avisos: [], violacoes: [], blocos: [] };
    }

    get hasPermissaoEditar(): boolean {
        return SiteUtils.hasPermissao([PerfilConfig.Admin, PerfilConfig.ChefeSTE, PerfilConfig.AuxSTE], this.perfis);
    }

    get hasPermissaoPublicar(): boolean {
        return SiteUtils.hasPermissao([PerfilConfig.Admin, PerfilConfig.ChefeSTE], this.perfis);
    }

    get isAdmin(): boolean {
        return SiteUtils.hasPermissao([PerfilConfig.Admin ], this.perfis);
    }

    get statusId(): number {
        return this.model ? statusQtsId(this.model.status) : 0;
    }

    get mostrarGerar(): boolean { return this.model ? podeGerar(this.model.status) : false; }
    get mostrarValidar(): boolean { return this.model ? podeValidar(this.model.status) : false; }
    get mostrarPublicar(): boolean { return this.model ? podePublicar(this.model.status) : false; }
    get mostrarCancelar(): boolean { return this.model ? podeCancelar(this.model.status) : false; }
    // Pode apagar alocações em qualquer status, exceto PUBLICADO (4).
    get podeLimparAlocacoes(): boolean { return !!this.model && (this.statusId !== 4 || this.isAdmin ); }

    get turmaId() {
        return this.$route.params.id;
    }

    get turmaObj() {
        return { id: this.turmaId };
    }

    get statusLabel() {
        return this.model ? labelStatusQts(this.model.status) : '';
    }

    get badgeClass() {
        const v = this.model && typeof this.model.status === 'object' ? this.model.status.id : this.model?.status;
        switch (Number(v)) {
            case 1: return 'badge-light';            // RASCUNHO
            case 2: return 'badge-light-primary';    // GERADO
            case 3: return 'badge-light-info';       // VALIDADO
            case 4: return 'badge-light-success';    // PUBLICADO
            case 5: return 'badge-light-danger';     // CANCELADO
            default: return 'badge-light';
        }
    }

    get periodo() {
        const ini = this.fmt(this.model?.inicio);
        const fim = this.fmt(this.model?.fim);
        return ini || fim ? `${ini} a ${fim}` : 'Período não definido';
    }

    get quadroBase() {
        return this.model?.quadroHorario?.descricao ?? '';
    }

    private fmt(v: any) {
        if (!v) return '';
        const s = String(v).substring(0, 10);
        const p = s.split('-');
        return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : s;
    }

    created() {
        this.buscar();
    }

    public buscar() {
        this.processando = true;
        this.service.getModel(this.$route.params.qtsSemanalId)
            .then(r => {
                this.model = JSON.parse(JSON.stringify(r));
                this.processando = false;
            })
            .catch(() => { this.processando = false; });
    }

    public exportarPdf() {
        if (this.processando || !this.model) return;
        const url = this.service.getPdfUrl(this.model.id, this.token);
        window.open(url, '_blank');
    }

    public exportarPdfPorHorario() {
        if (this.processando || !this.model) return;
        const url = this.service.getPdfPorHorarioUrl(this.model.id, this.token);
        window.open(url, '_blank');
    }

    public limparAlocacoes() {
        if (this.processando || !this.model) return;
        SiteUtils.alertConfimacao('Apagar todas as alocações deste QTS?',
            'Esta ação não pode ser desfeita. Um QTS gerado/validado voltará a RASCUNHO.',
            () => {
                this.processando = true;
                this.service.limparAlocacoes(this.model.id)
                    .then(response => {
                        this.processando = false;
                        SiteUtils.toastSucesso('Alocações apagadas!', `${response?.removidas ?? 0} alocação(ões) removida(s).`);
                        this.reloadGrade = !this.reloadGrade;
                        this.buscar();
                    })
                    .catch(error => {
                        this.processando = false;
                        SiteUtils.toastErro('Erro ao apagar alocações', error?.data?.message);
                    });
            }
        );
    }

    private abrirDiagnostico() {
        this.$nextTick(() => (this.$refs.abrirDiagnostico as HTMLElement)?.click());
    }

    public gerar() {
        if (this.processando || !this.model) return;
        SiteUtils.alertConfimacao('Gerar o QTS para esta configuração?', 'As alocações serão (re)geradas.',
            () => {
                this.processando = true;
                this.service.gerar(this.model.id)
                    .then(response => {
                        this.processando = false;
                        const r = response || {};
                        this.diagnostico = {
                            titulo: 'Resultado da geração',
                            ok: !!r.sucesso,
                            mensagem: r.mensagem || (r.sucesso ? 'Geração concluída.' : 'Não foi possível gerar o QTS.'),
                            meta: `Iterações: ${r.iteracoes ?? 0} · Tempo: ${r.tempoMs ?? 0} ms · Alocações: ${(r.alocacoes || []).length}`,
                            erros: r.erros || [],
                            avisos: r.avisos || [],
                            violacoes: [],
                            blocos: r.blocosNaoAlocados || [],
                        };
                        if (r.sucesso) {
                            SiteUtils.toastSucesso('Geração concluída!', r.mensagem);
                            this.$router.push({ name: 'sigap_turma_qts_gerador_qtssemanal_show_alocacoes' });
                            this.reloadGrade = !this.reloadGrade;
                        } else {
                            SiteUtils.toastErro('Geração não concluída', r.mensagem);
                        }
                        this.abrirDiagnostico();
                        this.buscar();
                    })
                    .catch(error => {
                        this.processando = false;
                        SiteUtils.toastErro('Erro ao gerar', error?.data?.message);
                    });
            }
        );
    }

    public validar() {
        if (this.processando || !this.model) return;
        this.processando = true;
        this.service.validar(this.model.id)
            .then(response => {
                this.processando = false;
                const r = response || {};
                this.diagnostico = {
                    titulo: 'Resultado da validação',
                    ok: !!r.ok,
                    mensagem: r.mensagem || (r.ok ? 'QTS validado.' : 'O QTS possui violações.'),
                    meta: '',
                    erros: [],
                    avisos: [],
                    violacoes: r.violacoes || [],
                    blocos: [],
                };
                if (r.ok) SiteUtils.toastSucesso('QTS validado!', r.mensagem);
                else SiteUtils.toastErro('Validação reprovada', r.mensagem);
                this.abrirDiagnostico();
                this.buscar();
            })
            .catch(error => {
                this.processando = false;
                SiteUtils.toastErro('Erro ao validar', error?.data?.message);
            });
    }

    public publicar() {
        if (this.processando || !this.model) return;
        SiteUtils.alertConfimacao('Publicar o QTS?', 'Após publicado, o QTS é congelado e não pode ser alterado.',
            () => {
                this.processando = true;
                this.service.publicar(this.model.id)
                    .then(response => {
                        this.processando = false;
                        SiteUtils.toastSucesso('QTS publicado!', response?.message);
                        this.buscar();
                    })
                    .catch(error => {
                        this.processando = false;
                        SiteUtils.toastErro('Erro ao publicar', error?.data?.message);
                    });
            }
        );
    }

    public cancelar() {
        if (this.processando || !this.model) return;
        SiteUtils.alertConfimacao('Cancelar o QTS?', 'O QTS será marcado como cancelado.',
            () => {
                this.processando = true;
                this.service.cancelar(this.model.id)
                    .then(response => {
                        this.processando = false;
                        SiteUtils.toastSucesso('QTS cancelado!', response?.message);
                        this.buscar();
                    })
                    .catch(error => {
                        this.processando = false;
                        SiteUtils.toastErro('Erro ao cancelar', error?.data?.message);
                    });
            }
        );
    }
}
